import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

const PRODUCTIVITY_COLORS: Record<string, string> = {
  productive: 'var(--green)',
  neutral: 'var(--blue)',
  unproductive: 'var(--red)',
};

export function WebsPage() {
  const [topDomains, setTopDomains] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get('/users').then(r => setUsers(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const url = selectedUser
      ? `/webs/top-domains?date=${date}&userId=${selectedUser}`
      : `/webs/company-top?date=${date}`;
    api.get(url).then(r => setTopDomains(r.data)).finally(() => setLoading(false));
  }, [date, selectedUser]);

  const formatTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const maxSecs = topDomains[0]?.totalSecs || 1;

  return (
    <div style={{ padding: '28px 32px' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Navegación web</h1>
          <p className="page-subtitle">Dominios visitados y tiempo por sitio</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="input" style={{ width: 180 }} value={selectedUser} onChange={e => setSelectedUser(e.target.value)}>
            <option value="">Todos los empleados</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <input type="date" className="input" style={{ width: 160 }} value={date} onChange={e => setDate(e.target.value)} />
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Dominios visitados', value: topDomains.length, color: 'var(--text)' },
          { label: 'Tiempo productivo', value: formatTime(topDomains.filter(d => d.productivity === 'productive').reduce((s, d) => s + +d.totalSecs, 0)), color: 'var(--green)' },
          { label: 'Tiempo improductivo', value: formatTime(topDomains.filter(d => d.productivity === 'unproductive').reduce((s, d) => s + +d.totalSecs, 0)), color: 'var(--red)' },
        ].map(c => (
          <div key={c.label} className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Domains list */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 13, fontWeight: 700 }}>Top dominios</h3>
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Cargando...</div>
        ) : topDomains.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🌐</div>
            Sin datos de navegación para este día
          </div>
        ) : (
          <div style={{ padding: '8px 0' }}>
            {topDomains.map((domain, i) => {
              const pct = Math.round((+domain.totalSecs / maxSecs) * 100);
              const color = PRODUCTIVITY_COLORS[domain.productivity] || 'var(--text3)';
              return (
                <div key={i} style={{ padding: '10px 20px', borderBottom: '1px solid var(--border2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{domain.domain}</div>
                    </div>
                    {domain.userCount && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{domain.userCount} usuarios</span>}
                    <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>{formatTime(+domain.totalSecs)}</span>
                  </div>
                  <div style={{ height: 3, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Note about URL tracking */}
      <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 10, fontSize: 12, color: 'var(--text2)' }}>
        ℹ️ <strong>Nota técnica:</strong> La detección de URLs requiere la extensión de Chrome/Firefox de AccioTrack o la API de accesibilidad de Windows. Los datos se registran automáticamente cuando el agente detecta cambios de pestaña.
      </div>
    </div>
  );
}
